/* ==========================================================================
   Searcher > Client > Calendar
   ========================================================================== */
/* Modules */
const { html } = require('common-tags');
const { DateTime } = require('luxon');

const { addData, addStyle } = require('@bluekiri/alexandria/helpers/dataHelpers');

const CalendarTemplate = require('@bluekiri/alexandria-contrib-components/components/calendar-rxjs/template');
const CalendarClient = require('@bluekiri/alexandria-contrib-components/components/calendar-rxjs/client/js');

const { types } = require('../store/actions');
const InputPanelClient = require('../../../../input-panel/client/js');
const InputPanelActions = require('../../../../input-panel/client/js/store/actions');

const { formatDate } = require('../helpers');
const { validateCalendar } = require('../validations');

const { CALENDAR } = require('../../../constants');
const configuration = require('../config');

function setDates(instance) {
    const { calendarInput } = instance;

    if (!calendarInput) return;

    const { dates = [] } = instance.store.select('data');

    const [startDate, endDate] = dates;

    /* Dates are undefined */
    if (!startDate && !endDate) {
        calendarInput.actions.clear();
        return;
    }

    if (startDate > endDate) return;

    /* Formate dates */
    const startDateValue = startDate ? formatDate(instance, DateTime.fromISO(startDate)) : '';
    const endDateValue = endDate ? formatDate(instance, DateTime.fromISO(endDate)) : '';

    /* Set input dates */
    calendarInput.actions.changeValue(`${startDateValue} → ${endDateValue}`);
}

function changeDate(instance, selectedDates) {
    const { config = {} } = instance;
    const { dates: { releaseDays = 0, sameDay = false } = {} } = config;

    /* Data */
    const { route = {} } = instance.store.select('data');
    const { returnDates = [] } = route;

    /* Disable days: release days and same selected day */
    let otherReturnDisableDays = [];

    /* First change, first day selected */
    if (selectedDates.length === 1) {
        /* Prepare release days if have */
        if (releaseDays > 0) {
            otherReturnDisableDays = [...Array(releaseDays)].map((element, ind) =>
                formatDate(
                    instance,
                    DateTime.fromJSDate(selectedDates[0]).plus({ days: ind + 1 }),
                    CALENDAR.DATE_FORMAT
                )
            );
        }

        /* prepare return days */
        /* Merge return days with otherReturnDisableDays and days after to selected day */
        const datesEnable = returnDates
            .filter(day => !otherReturnDisableDays.some(disableDay => disableDay === day.value))
            .filter(day => DateTime.fromISO(day.value) > DateTime.fromJSDate(selectedDates[0]));

        /* Return on same day? */
        if (sameDay) {
            datesEnable.push({
                value: formatDate(instance, DateTime.fromJSDate(selectedDates[0]), CALENDAR.DATE_FORMAT)
            });
        }

        /* Dispatch set data datesEnable, when the user select the first date we will set to enable the return dates */
        instance.actions.setData({ datesEnable });

        /* Redraw calendar to remove departure operative */
        instance.calendar.fp.redraw();

        /* Disable days with out operative */
        [...instance.calendar.element.querySelectorAll(`[class="flatpickr-day"][aria-label]`)].forEach(day => {
            disableDays(instance, day);
        });

        /* Load date on input */
        instance.calendarInput.actions.changeValue(`${formatDate(instance, DateTime.fromJSDate(selectedDates[0]))} → `);
    }
}

function updateDates(instance) {
    const selectedDates = instance.calendar.fp.selectedDates || [];

    if (!selectedDates.length) return;

    if (selectedDates.length === 1) {
        setDates(instance);
        return;
    }

    /* Formating Dates */
    const startDate = formatDate(instance, DateTime.fromJSDate(selectedDates[0]), CALENDAR.DATE_FORMAT);
    const endDate = formatDate(instance, DateTime.fromJSDate(selectedDates[1]), CALENDAR.DATE_FORMAT);

    /* Dispatch change Data */
    instance.actions.changeData({ dates: [startDate, endDate] });

    /* Sync calendar input */
    setDates(instance);
}

function disableDays(instance, dayElem) {
    const { datesEnable = [] } = instance.store.select('data');

    if (datesEnable.length === 0) return;

    const date = dayElem.getAttribute('aria-label');
    const isDisable = !datesEnable.some(item => item.value === date);

    /* add style class to put day as disable */
    if (date !== null && isDisable) {
        dayElem.classList.add('flatpickr-disabled');
    }
}

/**
 * render calendar-rxjs.
 * @param {Object} Instance component client instance
 * @return {String} String template calendar-rxjs
 */
function renderCalendar(instance) {
    const maxDateDefault = formatDate(
        instance,
        DateTime.fromJSDate(new Date(new Date().setMonth(new Date().getMonth() + 12))),
        CALENDAR.DATE_FORMAT
    );

    const { config = {}, store = {} } = instance;
    const { context = {}, dates: { minDate, maxDate = maxDateDefault } = {}, onModal } = config;

    /* Data store */
    const { dates = [] } = store.select('data');

    const startDate = !dates ? undefined : dates[0];
    const endDate = !dates ? undefined : dates[1];
    const showMonths = onModal ? 1 : 2;

    /* Data calendar */
    const dataCalendar = addStyle(
        addData(
            {
                config: {
                    mode: 'range',
                    showMonths
                },
                minDate,
                maxDate,
                startDate,
                endDate
            },
            { type: configuration().type.calendar }
        ),
        [onModal ? configuration().css.calendarFp : '']
    );

    return CalendarTemplate(dataCalendar, context);
}

/**
 * Init calendar-rxjs.
 * @param {Object} Instance component client instance
 * @return {void}
 */
function initDates(instance) {
    const inst = instance;
    const { calendarInput } = instance;

    /* Data Panel */
    const { dates = [] } = instance.store.select('data');
    const { panelType } = calendarInput.store.select('data');
    const panelSelector = `[data-type="${panelType}"]`;

    /* Init calendar */
    inst.calendar = CalendarClient(
        `${panelSelector} [data-component="CalendarRxjs"][data-type="${configuration().type.calendar}"]`,
        {
            fp: {
                ...CALENDAR,
                onChange(selectedDates) {
                    changeDate(inst, selectedDates);

                    if (selectedDates.length === 2) {
                        instance.calendarInput.actions.closePanel();
                    }
                },
                // eslint-disable-next-line max-params
                onDayCreate: (dObj, dStr, fp, dayElem) => {
                    disableDays(instance, dayElem);
                }
            }
        }
    );

    if (!inst.calendar) return;

    /* Jump to first day */
    if (dates && dates !== null) inst.calendar.fp.jumpToDate(dates[0]);
}

/**
 * Append ui-panel-rxj to  input.
 * @summary init panel, init calendar, close events, event subcriptions
 * @param {Object} Instance component client instance
 * @return {void}
 */
function addPanel(instance) {
    const inst = instance;
    const { calendarInput } = instance;

    const contentTemplate = html(
        `<div class='input-panel__body' ${configuration().hooks.panelBody}>${renderCalendar(instance)}</div>`
    );

    /* add accomodation to panel */
    instance.calendarInput.actions.setContent(contentTemplate);

    inst.hooks = {
        ...inst.hooks,
        panelBody: calendarInput.element.querySelector(`[${configuration().hooks.panelBody}]`)
    };

    /* Init */
    initDates(instance);
}

/**
 * initCalendar UiPanelCalendar
 * @param {Object} Instance - component client instance.
 * @summary Init input calendar: events open calendar, subcriptions to open calendar
 * @return {void}
 */
function initCalendar(instance) {
    const inst = instance;
    const { config = {}, selector } = instance;
    const { dates: calendarConfig = {} } = config;

    const { startDate, endDate } = calendarConfig;

    inst.calendarInput = InputPanelClient(
        `${selector} [data-component="InputPanel"][data-type="${configuration().type.calendar}"]`
    );

    if (!inst.calendarInput) return;

    /* Sync store */
    if (startDate && endDate) {
        instance.actions.setData({ dates: [startDate, endDate] });

        /* set input calendar value */
        setDates(instance);
    }

    /* Extends reactios */
    inst.reactions = {
        ...inst.reactions,
        changeDates: inst.store.subscribe((state, action) => {
            if (action.type !== types.CHANGE_DATA) return;
            if (!action.payload.dates) return;

            setDates(inst);
            validateCalendar(inst);
        }),
        openCalendar: inst.calendarInput.store.subscribe((state, action) => {
            if (action.type !== InputPanelActions.types.OPEN_PANEL) return;

            addPanel(inst);
            inst.actions.openPanel('calendarInput');
        }),
        closeCalendar: inst.calendarInput.store.subscribe((state, action) => {
            if (action.type !== InputPanelActions.types.CLOSE_PANEL) return;

            /* Destroy */
            destroyCalendar(inst);
            inst.actions.closePanel('calendarInput');
        }),
        destroyInputCalendar: inst.calendarInput.store.subscribe((state, action) => {
            if (action.type !== InputPanelActions.types.DESTROY) return;

            destroyInputCalendar(inst);
        })
    };
}

/**
 * Destroy panel calendar and his components.
 * @summary destroy ui-panel-rxjs, calendar-rxjs and set data enable dates with the
 *          departure enable dates
 * @param {Object} instance - component client instance
 * @return {void}
 */
function destroyCalendar(instance) {
    const result = instance;

    if (!instance.calendar) return;

    /* Data store */
    const { route = {} } = instance.store.select('data');

    /* Sync input value */
    updateDates(instance);

    result.calendar.actions.destroy();

    delete result.reactions.changeCalendar;
    delete result.calendar;

    /* Dispatch change Data ensure that enable date are of the departure */
    if (route.outboundDates) instance.actions.changeData({ datesEnable: route.outboundDates });
}

function destroyInputCalendar(instance) {
    const inst = instance;

    if (!instance.calendarInput) return;

    /* Remove References */
    delete inst.calendarInput;

    /* Destroy */
    destroyCalendar(inst);
}

module.exports = {
    initCalendar
};
